import React, { useState, useEffect } from "react"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "src/components/ui/dialog"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"

import { useCreateBooking } from "../hooks/useBookings"
import { fetchAvailableUnits } from "../api/bookingsApi"
import { fetchProjects } from "src/features/projects/api/projectsApi"

// Books a unit against a won deal. If the deal already has a project, units are
// scoped to it; otherwise the user picks a project first.
export function CreateBookingDialog({ open, deal, onClose, onSuccess }) {
  const { toast } = useToast()
  const { mutate, isPending } = useCreateBooking()

  const [projects, setProjects] = useState([])
  const [projectId, setProjectId] = useState(null)
  const [units, setUnits] = useState([])
  const [unitId, setUnitId] = useState(null)
  const [bookingAmount, setBookingAmount] = useState("")
  const [bookingDate, setBookingDate] = useState(() => new Date().toISOString().slice(0, 10))

  // Seed project from the deal; load the project list as a fallback picker.
  useEffect(() => {
    if (!open) return
    setProjectId(deal?.project_id || null)
    setUnitId(null)
    setBookingAmount("")
    fetchProjects({ per_page: 100 }).then((d) => setProjects(d.projects || [])).catch(() => {})
  }, [open, deal])

  // Load available units whenever the chosen project changes.
  useEffect(() => {
    if (!open || !projectId) { setUnits([]); return }
    fetchAvailableUnits(projectId).then(setUnits).catch(() => setUnits([]))
  }, [open, projectId])

  const selectedUnit = units.find((u) => u.id === unitId)
  const needsPaymentPlan = selectedUnit && !selectedUnit.has_payment_plan

  async function handleSubmit() {
    if (!unitId) {
      toast({ variant: "destructive", title: "Pick a unit to book" })
      return
    }
    if (needsPaymentPlan) {
      toast({
        variant: "destructive",
        title: "Payment plan required",
        description: "Configure a payment plan for this unit before booking.",
      })
      return
    }
    try {
      const booking = await mutate({
        deal_id: deal.id,
        unit_id: unitId,
        booking_amount: bookingAmount || 0,
        booking_date: bookingDate,
      })
      toast({ title: "Booking created", description: "Confirmation letter is being generated." })
      onSuccess?.(booking)
      onClose()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : "Could not create booking"
      toast({ variant: "destructive", title: "Booking failed", description: msg })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create booking</DialogTitle>
          <DialogDescription>
            Allot a unit to {deal?.lead?.name || "the customer"} from this won deal.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {!deal?.project_id && (
            <div className="space-y-1.5">
              <Label>Project</Label>
              <Select value={projectId ? String(projectId) : ""} onValueChange={(v) => setProjectId(Number(v))}>
                <SelectTrigger><SelectValue placeholder="Select a project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Unit</Label>
            <Select value={unitId ? String(unitId) : ""} onValueChange={(v) => setUnitId(Number(v))} disabled={!projectId}>
              <SelectTrigger>
                <SelectValue placeholder={projectId ? "Select an available unit" : "Pick a project first"} />
              </SelectTrigger>
              <SelectContent>
                {units.length === 0 && <div className="px-2 py-3 text-center text-xs text-slate-400">No available units</div>}
                {units.map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    {u.unit_number}{u.unit_type ? ` · ${u.unit_type}` : ""}
                    {!u.has_payment_plan ? " · no payment plan" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {needsPaymentPlan && (
              <p className="text-[12px] text-red-600">
                This unit has no payment plan. Configure one under{" "}
                <span className="font-medium">Configurations → Payment Plans</span> before booking.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Booking amount (₹)</Label>
              <Input
                type="number"
                placeholder="0"
                value={bookingAmount}
                onChange={(e) => setBookingAmount(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Booking date</Label>
              <Input type="date" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isPending || !unitId || needsPaymentPlan}>
            {isPending ? "Booking…" : "Create booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
